import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { getProjectById, Project } from '../../firebase/projectService';
import { CheckBadgeIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { PLATFORM_META, normalizeProjectLinks } from '../../utils/projectLinks';
import ProjectImage from '../../components/ProjectImage';

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setIsLoading(true);
        const fetchedProject = await getProjectById(id || '');
        setProject(fetchedProject);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  useEffect(() => {
    if (!isLoading && !project) {
      // Wait a bit before redirecting to projects page
      const timer = setTimeout(() => {
        navigate('/projects');
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, project, navigate]);

  if (isLoading) {
    return (
      <div className="container py-16 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-16">
        <div className="bg-red-100 border border-red-400 text-red-700 p-6 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>{error instanceof Error ? error.message : 'Failed to load project data'}</p>
          <p className="mt-4">Redirecting to projects page...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container py-16">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 p-6 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">Project Not Found</h2>
          <p>The project you're looking for doesn't exist or has been removed.</p>
          <p className="mt-4">Redirecting to projects page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-16">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero section */}
        <div className="mb-12">
          <h1 className="section-title">
            {project.title}
            {project.featured && (
              <span className="inline-flex items-center ml-3 text-yellow-500" title="Featured Project">
                <Star className="mr-1" size={20} />
                <span className="text-base font-medium">Featured</span>
              </span>
            )}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mt-4">
            {project.description}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {project.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-md text-sm font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Main image */}
        <div className="mb-12 rounded-lg overflow-hidden shadow-lg">
          <ProjectImage
            src={project.image}
            alt={project.title}
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Project details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2">
            {/* My Role — highlighted so recruiters instantly see the candidate's contribution */}
            {(project.myRole || (project.contributions && project.contributions.length > 0)) && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="relative mb-8 rounded-2xl p-[1.5px] bg-gradient-to-br from-primary-500 via-primary-400 to-purple-500 shadow-lg"
              >
                <div className="rounded-2xl bg-white dark:bg-gray-800 p-6 sm:p-8 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-primary-500 to-purple-500 text-white shadow-md flex-shrink-0">
                      <UserCircleIcon className="h-6 w-6" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
                        My Role
                      </p>
                      {project.myRole ? (
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                          {project.myRole}
                        </h2>
                      ) : (
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                          What I Did
                        </h2>
                      )}
                    </div>
                  </div>

                  {project.contributions && project.contributions.length > 0 && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-5">
                      {project.contributions.map((item, index) => (
                        <li key={index} className="flex items-start gap-2.5">
                          <CheckBadgeIcon className="h-5 w-5 text-primary-500 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            )}

            <h2 className="text-2xl font-bold mb-4">About the Project</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 whitespace-pre-line">
              {project.longDescription}
            </p>
          </div>

          <div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6">
              <h2 className="text-2xl font-bold mb-4">Key Features</h2>
              {project.features && project.features.length > 0 ? (
                <div className="space-y-4">
                  {project.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="border-l-4 border-primary-500 pl-4">
                      <h3 className="font-semibold text-primary-600 dark:text-primary-400 mb-2">
                        {feature.name}
                      </h3>
                      <ul className="space-y-1">
                        {feature.subFeatures.map((subFeature, subIndex) => (
                          <li key={subIndex} className="flex items-start space-x-2">
                            <CheckBadgeIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-300 text-sm">{subFeature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No features listed for this project.</p>
              )}
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-4">Technologies Used</h2>
              <ul className="space-y-2">
                {project.technologies.map((tech, index) => (
                  <li key={index} className="text-gray-600 dark:text-gray-400">
                    • {tech}
                  </li>
                ))}
              </ul>

              {(() => {
                const projectLinks = normalizeProjectLinks(project);
                return (projectLinks.length > 0 || project.github) && (
                  <div className="mt-8 space-y-3">
                    <h3 className="text-lg font-semibold">Links</h3>
                    {projectLinks.map((link) => {
                      const meta = PLATFORM_META[link.type];
                      const Icon = meta.Icon;
                      return (
                        <a
                          key={link.type}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ backgroundColor: meta.color }}
                          className="flex items-center justify-center gap-2 w-full py-2 px-4 text-white text-center rounded-md hover:opacity-90 transition-opacity"
                        >
                          <Icon className="w-5 h-5" />
                          {meta.ctaLabel}
                        </a>
                      );
                    })}
                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-2 px-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white text-center rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        View Source Code
                      </a>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Gallery */}
        {project.gallery && project.gallery.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Project Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {project.gallery.map((image, index) => (
                <div key={index} className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src={image}
                    alt={`${project.title} screenshot ${index + 1}`}
                    className="w-full h-auto object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ProjectDetailPage; 